// v5.0 — Kiosque AYA : refonte fidèle à la planche (thème clair, hero sombre, à la une, magazines, catégories).
// browse → detail (page dédiée) → reading (lecteur immersif). Catalogue 100% backend.
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Newspaper, BookOpen, Book, Drama, Smile, Search, Bell, BellRing, CalendarDays, Eye, ChevronRight,
  Home, Library, ShoppingBag, Lightbulb, Headphones, X, GraduationCap,
} from "lucide-react";
import ContentDetail from "@/components/content/ContentDetail";
import ContentReader from "@/components/content/ContentReader";
import ContinueReadingStrip from "@/components/content/ContinueReadingStrip";
import CatalogCrossPromo from "@/components/content/CatalogCrossPromo";
import AyaMenu from "@/components/AyaMenu";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const uid = () => { try { return localStorage.getItem("jsonfirst_civ_userid"); } catch { return null; } };
const cover = (u) => (u && u.startsWith("http") ? u : `${BACKEND_URL}${u}`);
const fdate = (d) => (d ? new Date(d) : new Date()).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }).replace(".", "");

const HERO_SLIDES = [
  { img: "/aya/kiosque/kq_hero.jpg", badge: null, t1: "L'actualité", t2: "d'ici et d'ailleurs", sub: "Journaux, magazines, livres, contes, tout dans votre kiosque AYA.", cta: "Explorer le kiosque", Icon: Search, to: "tout" },
  { img: "/aya/kiosque/kq_promo_presse.jpg", badge: "PROMO DU JOUR", t1: "Les journaux du jour", t2: "à 300 FCFA", sub: "Fraternité Matin, Le Jour Plus, Soir Info… parus ce matin.", cta: "Lire la presse", Icon: Newspaper, to: "presse" },
  { img: "/aya/kiosque/kq_promo_audio.jpg", badge: "PROMO DU JOUR", t1: "Contes & livres audio", t2: "dès 800 FCFA", sub: "Écoutez notre héritage, partout, à tout moment.", cta: "Découvrir", Icon: Headphones, to: "audio" },
  { img: "/aya/kiosque/kq_promo_edu.jpg", badge: "PROMO DU JOUR", t1: "Réussir l'école", t2: "avec AYA", sub: "Manuels et soutien scolaire dès 3 000 FCFA.", cta: "Voir Éducation", Icon: GraduationCap, to: "education" },
];

const TABS = [
  { id: "presse", label: "Presse", Icon: Newspaper },
  { id: "magazines", label: "Magazines", Icon: BookOpen },
  { id: "livres", label: "Livres", Icon: Book },
  { id: "contes", label: "Contes & Traditions", Icon: Drama },
  { id: "jeunesse", label: "Jeunesse", Icon: Smile },
];
const CATS = [
  { id: "romans", label: "Romans", sub: "Histoires captivantes", Icon: Book, bg: "#2563EB" },
  { id: "stories", label: "AYA Stories", sub: "Des histoires locales", Icon: Lightbulb, bg: "#10B981" },
  { id: "audio", label: "Livres audio", sub: "Écoutez partout", Icon: Headphones, bg: "#F97316" },
  { id: "contes", label: "Contes & Traditions", sub: "Notre héritage culturel", Icon: Drama, bg: "#8B5CF6" },
  { id: "jeunesse", label: "Jeunesse", sub: "Pour les petits", Icon: Smile, bg: "#EC4899" },
];

function SecHead({ title, onSeeAll, testid }) {
  return (
    <div className="flex items-center justify-between mb-2.5">
      <h2 className="text-[17px] font-extrabold text-[#10241A] m-0">{title}</h2>
      <button onClick={onSeeAll} data-testid={testid} className="text-[12px] font-bold text-[#0E7A4F] flex items-center gap-0.5">Voir tout <ChevronRight size={14} /></button>
    </div>
  );
}

export default function MediaHubPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("browse");
  const [activeContent, setActiveContent] = useState(null);
  const [activeOwned, setActiveOwned] = useState(false);
  const [activeReaderId, setActiveReaderId] = useState(null);
  const [readerStartPage, setReaderStartPage] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [items, setItems] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [slide, setSlide] = useState(0);
  const [alertIds, setAlertIds] = useState([]);
  const [fresh, setFresh] = useState([]);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const userId = uid();
    if (!userId) return;
    axios.get(`${BACKEND_URL}/api/v3/kiosque/alerts`, { params: { user_id: userId } })
      .then((r) => { if (r.data?.ok) setAlertIds(r.data.ids || []); }).catch(() => {});
    axios.get(`${BACKEND_URL}/api/v3/kiosque/alerts/digest`, { params: { user_id: userId } })
      .then((r) => { if (r.data?.ok && r.data.fresh?.length) setFresh(r.data.fresh); }).catch(() => {});
  }, []);

  const toggleAlert = async (it, e) => {
    e.stopPropagation();
    const userId = uid();
    if (!userId) return navigate("/login?next=/media");
    try {
      const r = await axios.post(`${BACKEND_URL}/api/v3/kiosque/alerts/toggle`, { user_id: userId, content_id: it.id, title: it.title });
      if (!r.data?.ok) return;
      if (r.data.on) {
        setAlertIds((ids) => [...ids, it.id]);
        toast.success(`Alerte activée : vous serez prévenu chaque matin de la nouvelle édition de ${it.title} ✓`);
      } else {
        setAlertIds((ids) => ids.filter((x) => x !== it.id));
        toast(`Alerte désactivée pour ${it.title}`);
      }
    } catch { toast.error("Impossible de modifier l'alerte, réessayez."); }
  };

  useEffect(() => {
    if (!localStorage.getItem("civ_user_token")) navigate("/login?next=/media");
  }, [navigate]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/v3/content/discover`)
      .then((r) => setItems(Array.isArray(r.data?.items) ? r.data.items : []))
      .catch(() => {});
  }, [reloadKey]);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId) return;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/v3/content/${openId}`);
        let owned = false;
        const token = localStorage.getItem("civ_user_token");
        if (token) {
          try {
            const re = await axios.get(`${BACKEND_URL}/api/v3/content/entitlements`, { headers: { Authorization: `Bearer ${token}` } });
            owned = (re.data?.entitlements || []).some((e) => e.resource_id === openId);
          } catch (_) {}
        }
        setActiveContent(r.data?.content);
        setActiveOwned(owned);
        setMode("detail");
      } catch (_) {}
    })();
  }, [searchParams]);

  async function handleSelect(item, extras) {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/v3/content/${item.id}`);
      setActiveContent(r.data?.content);
      setActiveOwned(!!(extras && extras.owned));
      setMode("detail");
    } catch (_) {}
  }
  function openReader(resourceId, startPage = null) {
    setActiveReaderId(resourceId);
    setReaderStartPage(startPage);
    setMode("reading");
  }
  function handlePurchased() { setActiveOwned(true); setReloadKey((k) => k + 1); }
  function backToBrowse() {
    setActiveContent(null); setActiveReaderId(null); setReaderStartPage(null);
    setMode("browse"); setReloadKey((k) => k + 1);
  }
  const scrollLibrary = () => document.getElementById("kiosque-library")?.scrollIntoView({ behavior: "smooth" });

  if (mode === "detail" && activeContent) {
    return (
      <div className="min-h-screen bg-[#0a0a0d]">
        <ContentDetail content={activeContent} owned={activeOwned} onBack={backToBrowse}
          onRead={(page) => openReader(activeContent.id, page)} onPurchased={handlePurchased} />
        <div className="max-w-3xl mx-auto px-4 pb-40 pt-2 relative z-30">
          <CatalogCrossPromo src="journal" compact />
        </div>
      </div>
    );
  }
  if (mode === "reading" && activeReaderId) {
    return <ContentReader resourceId={activeReaderId} initialPage={readerStartPage} onClose={backToBrowse} />;
  }

  const presse = items.filter((it) => it.kind === "presse" || it.kind === "journal");
  const magazines = items.filter((it) => it.kind === "magazine");

  return (
    <div className="min-h-screen bg-[#F5F7F4] text-[#10241A]" data-testid="media-hub-page">
      <AyaMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* ── HEADER ── */}
      <div className="sticky top-0 z-20 bg-[#F5F7F4]/95 backdrop-blur border-b border-black/5">
        <div className="max-w-3xl mx-auto flex items-center gap-2.5 px-4 py-2.5">
          <span className="w-10 h-10 rounded-xl border border-black/10 bg-white flex items-center justify-center shrink-0">
            <Newspaper size={19} className="text-[#0E7A4F]" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[19px] font-black leading-tight"><span className="italic tracking-tight">AYA</span> Kiosque</div>
            <div className="text-[10.5px] text-black/45 font-medium -mt-0.5">Toute l'actualité en un seul endroit</div>
          </div>
          <button onClick={() => navigate("/kiosque/section/tout")} data-testid="kiosque-search-btn" aria-label="Rechercher" className="p-2"><Search size={20} /></button>
          <button onClick={() => setMenuOpen(true)} data-testid="kiosque-bell-btn" aria-label="Notifications" className="p-2 relative">
            <Bell size={20} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
          </button>
          <button onClick={() => navigate("/mon-compte")} data-testid="kiosque-avatar-btn" aria-label="Mon compte"
            className="w-9 h-9 rounded-full bg-[#0B1F14] border-2 border-[#0E7A4F]/40 flex items-center justify-center shrink-0 relative">
            <Drama size={17} className="text-amber-400" />
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-[#F5F7F4]" />
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-3 space-y-6 pb-28">
        {/* ── ONGLETS ── */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4" data-testid="kiosque-cat-tiles">
          {TABS.map(({ id, label, Icon }, i) => {
            const on = i === 0;
            return (
              <button key={id} data-testid={`kiosque-cat-${id}`}
                onClick={() => { if (!on) navigate(`/kiosque/section/${id}`); }}
                className={`shrink-0 min-w-[86px] h-[74px] rounded-2xl flex flex-col items-center justify-center gap-1.5 px-3 border transition-colors ${on ? "bg-[#0B1F14] border-[#0B1F14] text-white" : "bg-white border-black/10 text-[#10241A]"}`}>
                <Icon size={20} className={on ? "text-emerald-400" : ""} strokeWidth={1.9} />
                <span className="text-[11px] font-bold leading-tight text-center">{label}</span>
              </button>
            );
          })}
        </div>

        {/* ── BANDEAU ÉDITION DU MATIN ── */}
        {fresh.length > 0 && (
          <div className="flex items-center gap-2.5 rounded-2xl border border-[#0E7A4F]/35 bg-[#0E7A4F]/10 px-3.5 py-3" data-testid="kiosque-morning-banner">
            <span className="w-9 h-9 rounded-full bg-[#0E7A4F] flex items-center justify-center shrink-0"><BellRing size={16} className="text-white" /></span>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-extrabold text-[#0B3B27]">Édition du matin disponible</div>
              <div className="text-[10.5px] text-[#0B3B27]/70 font-semibold truncate">{fresh.map((f) => f.title).join(" · ")} — parue ce matin</div>
            </div>
            <button onClick={() => handleSelect({ id: fresh[0].content_id })} data-testid="kiosque-morning-read"
              className="rounded-full bg-[#0E7A4F] text-white text-[10.5px] font-extrabold px-3.5 py-2 shrink-0">Lire</button>
            <button onClick={() => setFresh([])} data-testid="kiosque-morning-dismiss" aria-label="Fermer" className="p-1 text-[#0B3B27]/50 shrink-0"><X size={15} /></button>
          </div>
        )}

        {/* ── HERO CARROUSEL ── */}
        <div className="relative rounded-3xl overflow-hidden bg-[#08170F] min-h-[196px]" data-testid="kiosque-hero">
          <div className="flex transition-transform duration-700 ease-out" style={{ transform: `translateX(-${slide * 100}%)` }}>
            {HERO_SLIDES.map((s, i) => (
              <div key={i} className="relative w-full shrink-0 min-h-[196px]" data-testid={`kiosque-hero-slide-${i}`}>
                <img src={s.img} alt="" className="absolute inset-0 w-full h-full object-cover object-right" loading={i === 0 ? "eager" : "lazy"} />
                <div className="absolute inset-0 bg-gradient-to-r from-[#04140B] via-[#04140B]/80 to-transparent" />
                <div className="relative p-5 max-w-[70%]">
                  {s.badge && <span className="inline-block text-[8.5px] font-black tracking-widest text-amber-300 border border-amber-400/50 rounded-full px-2.5 py-1 mb-1.5">{s.badge}</span>}
                  <h1 className="text-[23px] font-black leading-[1.08] text-white m-0">{s.t1}<br /><span className="text-emerald-300">{s.t2}</span></h1>
                  <p className="text-[11px] text-white/80 mt-1.5 leading-snug">{s.sub}</p>
                  <button onClick={() => navigate(`/kiosque/section/${s.to}`)} data-testid={`kiosque-hero-cta-${i}`}
                    className="mt-2.5 inline-flex items-center gap-2 rounded-full bg-[#0E7A4F] text-white text-[11.5px] font-extrabold px-4 py-2.5">
                    <s.Icon size={13} /> {s.cta}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="absolute bottom-2.5 right-4 flex gap-1.5">
            {HERO_SLIDES.map((_, i) => (
              <button key={i} onClick={() => setSlide(i)} data-testid={`kiosque-hero-dot-${i}`} aria-label={`Visuel ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === slide ? "bg-emerald-400 w-4" : "bg-white/30 w-1.5"}`} />
            ))}
          </div>
        </div>

        {/* ── À LA UNE ── */}
        <div data-testid="kiosque-alaune">
          <SecHead title="À la une" testid="alaune-see-all" onSeeAll={() => navigate("/kiosque/section/presse")} />
          <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
            {presse.map((it) => (
              <div key={it.id} data-testid={`alaune-${it.id}`} className="shrink-0 w-[172px] rounded-2xl bg-white border border-black/8 shadow-sm p-2.5">
                <button onClick={() => handleSelect(it)} className="block w-full relative">
                  <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-black/5">
                    {it.cover_url && <img src={cover(it.cover_url)} alt={it.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />}
                  </div>
                  <span onClick={(e) => toggleAlert(it, e)} role="button" aria-label="Alerte édition du matin" data-testid={`kiosque-alert-${it.id}`}
                    className={`absolute top-1.5 right-1.5 w-8 h-8 rounded-full flex items-center justify-center shadow ${alertIds.includes(it.id) ? "bg-[#0E7A4F] text-white" : "bg-white/95 text-[#0B3B27]"}`}>
                    {alertIds.includes(it.id) ? <BellRing size={14} /> : <Bell size={14} />}
                  </span>
                </button>
                <div className="flex items-center gap-1 text-[10px] text-black/50 font-semibold mt-2"><CalendarDays size={11} /> {fdate(it.published_at)}</div>
                <div className="flex gap-1.5 mt-2">
                  <button onClick={() => handleSelect(it)} data-testid={`alaune-preview-${it.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-full border border-[#0E7A4F]/50 text-[#0E7A4F] text-[10px] font-extrabold py-2"><Eye size={11} /> Aperçu</button>
                  <button onClick={() => handleSelect(it)} data-testid={`alaune-read-${it.id}`}
                    className="flex-1 rounded-full bg-[#0E7A4F] text-white text-[10px] font-extrabold py-2">Lire {it.price_fcfa} FCFA</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── MAGAZINES POPULAIRES ── */}
        <div data-testid="kiosque-magazines">
          <SecHead title="Magazines populaires" testid="magazines-see-all" onSeeAll={() => navigate("/kiosque/section/magazines")} />
          <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
            {magazines.map((it) => (
              <button key={it.id} onClick={() => handleSelect(it)} data-testid={`magazine-${it.id}`} className="shrink-0 w-[124px] text-left">
                <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-black/5 border border-black/8 shadow-sm">
                  {it.cover_url && <img src={cover(it.cover_url)} alt={it.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />}
                </div>
                <div className="flex items-center gap-1 text-[9.5px] text-black/50 font-semibold mt-1.5"><CalendarDays size={10} /> {fdate(it.published_at)}</div>
              </button>
            ))}
          </div>
        </div>

        {/* ── CATÉGORIES ── */}
        <div data-testid="kiosque-popular">
          <SecHead title="Catégories" testid="categories-see-all" onSeeAll={() => navigate("/kiosque/section/tout")} />
          <div className="flex gap-2.5 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
            {CATS.map((c) => (
              <button key={c.id} onClick={() => navigate(`/kiosque/section/${c.id}`)} data-testid={`popular-${c.id}`}
                className="shrink-0 w-[118px] h-[128px] rounded-2xl p-3 text-left flex flex-col text-white" style={{ background: c.bg }}>
                <c.Icon size={22} strokeWidth={2} />
                <div className="text-[12.5px] font-extrabold leading-tight mt-2">{c.label}</div>
                <div className="text-[9.5px] text-white/85 leading-snug mt-1">{c.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* ── BIBLIOTHÈQUE / REPRENDRE LA LECTURE ── */}
        <div id="kiosque-library" className="rounded-2xl bg-[#0B1F14] p-3">
          <ContinueReadingStrip key={`c-${reloadKey}`} onResume={(id) => openReader(id, null)} />
        </div>
      </div>

      {/* ── NAV BASSE ── */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/97 backdrop-blur border-t border-black/8" data-testid="kiosque-bottom-nav">
        <div className="max-w-3xl mx-auto flex items-center justify-around px-2 py-1.5 relative">
          <button onClick={() => navigate("/")} data-testid="knav-home" className="flex flex-col items-center gap-0.5 px-2 py-1 text-black/55">
            <Home size={19} strokeWidth={1.9} /><span className="text-[9.5px] font-semibold">Accueil</span>
          </button>
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="knav-kiosque" className="flex flex-col items-center gap-0.5 px-2 py-1 text-[#0E7A4F]">
            <BookOpen size={19} strokeWidth={2.2} /><span className="text-[9.5px] font-extrabold">Kiosque</span>
          </button>
          <button onClick={() => navigate("/kiosque/catalogues?src=kiosque")} data-testid="knav-mask" aria-label="Catalogues commerçants"
            className="w-14 h-14 -mt-7 rounded-full bg-gradient-to-b from-emerald-500 to-[#0E7A4F] border-4 border-white flex items-center justify-center shadow-lg shadow-emerald-600/30">
            <Drama size={25} className="text-white" strokeWidth={1.8} />
          </button>
          <button onClick={scrollLibrary} data-testid="knav-library" className="flex flex-col items-center gap-0.5 px-2 py-1 text-black/55">
            <Library size={19} strokeWidth={1.9} /><span className="text-[9.5px] font-semibold">Bibliothèque</span>
          </button>
          <button onClick={scrollLibrary} data-testid="knav-purchases" className="flex flex-col items-center gap-0.5 px-2 py-1 text-black/55">
            <ShoppingBag size={19} strokeWidth={1.9} /><span className="text-[9.5px] font-semibold">Mes achats</span>
          </button>
        </div>
      </div>
    </div>
  );
}
